import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { LuX } from "react-icons/lu";
import ReactMarkdown from "react-markdown";
import { useIsCompactView } from "../core_stores/useIsCompactViewStore";

interface PrivacyTermsModalContextType {
  show: (content: string) => void;
}

const PrivacyTermsModalContext = createContext<
  PrivacyTermsModalContextType | undefined
>(undefined);

export function usePrivacyTermsModal() {
  const ctx = useContext(PrivacyTermsModalContext);
  if (!ctx)
    throw new Error(
      "usePrivacyTermsModal must be used within a PrivacyTermsModalProvider"
    );
  return ctx;
}

export function PrivacyTermsModalProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const isCompactView = useIsCompactView();

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setModalContent(null);
    }, 300);
  };

  const show = useCallback((content: string) => {
    setModalContent(content);
    setIsClosing(false);
    setIsOpen(true);
  }, []);

  return (
    <PrivacyTermsModalContext.Provider value={{ show }}>
      {children}
      {isOpen && modalContent && (
        <div
          className={`${
            isClosing ? "animate-fade-out" : "animate-fade-in"
          } fixed inset-0
              flex items-center justify-center
              bg-black/25 backdrop-blur-sm
              transition-opacity duration-300 z-50`}
          onClick={handleClose}
        >
          <div
            className={`${
              isCompactView
                ? "h-full w-full"
                : "h-[90vh] max-h-[90vh] max-w-3xl rounded-lg m-8"
            } relative 
                bg-light-surface-primary dark:bg-[#2B2E32]
                shadow-[0_4px_2.6px_0_rgba(0,0,0,0.46),0_3px_14.2px_0_rgba(0,0,0,0.3)]
                transform transition-all duration-300 ease-in-out
                overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 z-10
                  text-light-placeholder dark:text-dark-placeholder
                  hover:brightness-150 transition-all duration-300"
            >
              <LuX size={24} />
            </button>

            <div className="w-full h-full overflow-y-auto">
              <div className="p-8 pb-4">
                <div className="react-markdown">
                  <ReactMarkdown>{modalContent}</ReactMarkdown>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </PrivacyTermsModalContext.Provider>
  );
}
